// src/providers/remix-progress-provider.tsx
import React from "react";
import { ProgressProvider } from "@bprogress/react";

// src/components/remix-progress.tsx
import { useLocation } from "@remix-run/react";
import { useAnchorProgress, withMemo } from "@bprogress/react";
var RemixProgressComponent = (props) => {
  const routerLocation = useLocation();
  useAnchorProgress(
    {
      ...props,
      forcedStopDelay: 500
    },
    [routerLocation]
  );
  return null;
};
var RemixProgress = withMemo(RemixProgressComponent);
RemixProgress.displayName = "RemixProgress";

// src/providers/remix-progress-provider.tsx
var RemixProgressProvider = ({
  children,
  color,
  height,
  options,
  spinnerPosition,
  style,
  disableStyle,
  nonce,
  ...props
}) => {
  return /* @__PURE__ */ React.createElement(
    ProgressProvider,
    {
      color,
      height,
      options,
      spinnerPosition,
      style,
      disableStyle,
      nonce
    },
    /* @__PURE__ */ React.createElement(RemixProgress, { ...props }),
    children
  );
};

// src/index.tsx
import {
  useProgress as useProgress2,
  Progress,
  Bar,
  Peg,
  Spinner,
  SpinnerIcon,
  Indeterminate
} from "@bprogress/react";

// src/hooks/use-navigate.tsx
import { useCallback } from "react";
import { useNavigate as useRemixNavigate } from "@remix-run/react";
import { isSameURL } from "@bprogress/core";
import { useProgress } from "@bprogress/react";
function useNavigate(options) {
  const { customNavigate, ...defaultProgressOptions } = options || {};
  const {
    start,
    stop,
    disableSameURL: providerDisableSameURL,
    startPosition: providerStartPosition,
    delay: providerDelay,
    stopDelay: providerStopDelay
  } = useProgress();
  const useSelectedNavigate = useCallback(() => {
    if (customNavigate) return customNavigate();
    return useRemixNavigate();
  }, [customNavigate]);
  const navigate = useSelectedNavigate();
  const enhancedNavigate = useCallback(
    (to, navOptions, progressOptions) => {
      const mergedOptions = { ...defaultProgressOptions, ...progressOptions };
      const localDisableSameURL = mergedOptions.disableSameURL !== void 0 ? mergedOptions.disableSameURL : providerDisableSameURL;
      const localStartPosition = mergedOptions.startPosition !== void 0 ? mergedOptions.startPosition : providerStartPosition;
      const localDelay = mergedOptions.delay !== void 0 ? mergedOptions.delay : providerDelay;
      const localStopDelay = mergedOptions.stopDelay !== void 0 ? mergedOptions.stopDelay : providerStopDelay;
      if (typeof to === "number") {
        if (mergedOptions?.showProgress !== false) {
          start(localStartPosition, localDelay);
        }
        const result = navigate(to);
        if (mergedOptions?.showProgress !== false) {
          setTimeout(() => {
            stop(localStopDelay);
          }, localDelay || 0);
        }
        return result;
      } else {
        let targetUrl;
        try {
          if (typeof to === "string") {
            targetUrl = new URL(to, window.location.href);
          } else {
            const {
              pathname = window.location.pathname,
              search = "",
              hash = ""
            } = to;
            targetUrl = new URL(pathname + search + hash, window.location.href);
          }
        } catch (error) {
          console.error(error);
          return navigate(to, navOptions);
        }
        const currentUrl = new URL(window.location.href);
        const sameURL = isSameURL(targetUrl, currentUrl);
        if (sameURL && localDisableSameURL) {
          return navigate(to, navOptions);
        }
        if (mergedOptions?.showProgress !== false) {
          start(localStartPosition, localDelay);
        }
        const result = navigate(to, navOptions);
        if (sameURL && mergedOptions?.showProgress !== false) {
          setTimeout(() => {
            stop(localStopDelay);
          }, localDelay || 0);
        }
        return result;
      }
    },
    [
      defaultProgressOptions,
      providerDisableSameURL,
      providerStartPosition,
      providerDelay,
      providerStopDelay,
      navigate,
      start,
      stop
    ]
  );
  return enhancedNavigate;
}
export {
  Bar,
  Indeterminate,
  Peg,
  Progress,
  RemixProgressProvider as ProgressProvider,
  Spinner,
  SpinnerIcon,
  useNavigate,
  useProgress2 as useProgress
};
